import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Toolbar,
  AppBar,
  Snackbar,
  Tooltip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import { PageName, DatasetWithCount } from '../types';
import { DrawerNavigation, ConfirmDialog } from '../components';
import { DRAWER_WIDTH, COLLAPSED_WIDTH } from '../constants/ui';
import { listDatasets, createDataset, renameDataset, deleteDataset } from '../services/datasetService';
import { getErrorMessage } from '../utils/errorHandler';

interface PlateDatasetsProps {
  onLogout: () => void;
  onNavigate: (page: PageName, taskId?: string) => void;
}

const truncateId = (id: string): string => {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
};

const PlateDatasets: React.FC<PlateDatasetsProps> = ({ onLogout, onNavigate }) => {
  const [datasets, setDatasets] = useState<DatasetWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDatasetLabel, setNewDatasetLabel] = useState('');
  const [creating, setCreating] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDataset, setEditingDataset] = useState<DatasetWithCount | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [datasetToDelete, setDatasetToDelete] = useState<DatasetWithCount | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentDrawerWidth = drawerCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchDatasets = async () => {
      try {
        setLoading(true);
        const data = await listDatasets('plate');
        setDatasets(data.sort((a, b) => b.count - a.count));
      } catch (err) {
        console.error('Failed to fetch datasets:', err);
        setError('Failed to load datasets');
      } finally {
        setLoading(false);
      }
    };

    fetchDatasets();
  }, []);

  const handleCopyId = async (id: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(id);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = id;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setSnackbar({ open: true, message: 'ID copied to clipboard', severity: 'success' });
    } catch (err) {
      console.error('Failed to copy:', err);
      setSnackbar({ open: true, message: 'Failed to copy ID', severity: 'error' });
    }
  };

  const handleCreate = async () => {
    if (!newDatasetLabel.trim()) return;
    setCreating(true);
    try {
      const result = await createDataset('plate', newDatasetLabel.trim());
      const newDataset: DatasetWithCount = {
        id: result.id,
        label: newDatasetLabel.trim(),
        count: 0,
      };
      setDatasets((prev) => [...prev, newDataset].sort((a, b) => b.count - a.count));
      setCreateDialogOpen(false);
      setNewDatasetLabel('');
      setSnackbar({ open: true, message: 'Dataset created successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to create dataset:', err);
      setSnackbar({ open: true, message: 'Failed to create dataset', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (dataset: DatasetWithCount) => {
    setEditingDataset(dataset);
    setEditLabel(dataset.label);
    setEditDialogOpen(true);
  };

  const handleSaveRename = async () => {
    if (!editingDataset || !editLabel.trim()) return;
    setSaving(true);
    try {
      await renameDataset('plate', editingDataset.id, editLabel.trim());
      setDatasets((prev) =>
        prev.map((d) => (d.id === editingDataset.id ? { ...d, label: editLabel.trim() } : d))
      );
      setEditDialogOpen(false);
      setEditingDataset(null);
      setSnackbar({ open: true, message: 'Dataset renamed successfully', severity: 'success' });
    } catch (err) {
      console.error('Failed to rename dataset:', err);
      setSnackbar({ open: true, message: 'Failed to rename dataset', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (dataset: DatasetWithCount) => {
    setDatasetToDelete(dataset);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!datasetToDelete) return;
    setDeleting(true);
    try {
      await deleteDataset('plate', datasetToDelete.id);
      setDatasets((prev) => prev.filter((d) => d.id !== datasetToDelete.id));
      setDeleteDialogOpen(false);
      setDatasetToDelete(null);
      setSnackbar({ open: true, message: 'Dataset deleted successfully', severity: 'success' });
    } catch (err: any) {
      console.error('Failed to delete dataset:', err);
      const status = err?.response?.status;
      const message = status === 409
        ? 'Cannot delete: dataset still has samples attached'
        : getErrorMessage(err);
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const totalDatasets = datasets.length;
  const totalSamples = datasets.reduce((sum, d) => sum + d.count, 0);

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: '#ff9800',
          transition: 'width 0.2s, margin 0.2s',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <StorageIcon sx={{ mr: 1 }} />
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Plate Datasets
          </Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={onLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <DrawerNavigation
        currentPage="datasetsPlate"
        onNavigate={onNavigate}
        collapsed={drawerCollapsed}
        onToggleCollapse={() => setDrawerCollapsed(!drawerCollapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          bgcolor: 'background.default',
          minHeight: '100vh',
          transition: 'width 0.2s, margin 0.2s',
        }}
      >
        <Toolbar />
        <Container maxWidth="lg">
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 4, alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Datasets
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {totalDatasets}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Samples in Datasets
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {totalSamples.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
            >
              Create Dataset
            </Button>
          </Paper>

          <TableContainer component={Paper}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#ff9800' }} />
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Dataset Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Count
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datasets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No datasets found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    datasets.map((dataset) => (
                      <TableRow key={dataset.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title={dataset.id} placement="top">
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                              >
                                {truncateId(dataset.id)}
                              </Typography>
                            </Tooltip>
                            <Tooltip title="Copy ID">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyId(dataset.id)}
                                sx={{ p: 0.5 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={500}>{dataset.label}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography>{dataset.count.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Rename">
                            <IconButton size="small" onClick={() => handleEditClick(dataset)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={dataset.count > 0 ? 'Cannot delete: has samples' : 'Delete'}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(dataset)}
                                disabled={dataset.count > 0}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Container>
      </Box>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create New Dataset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Dataset Label"
            value={newDatasetLabel}
            onChange={(e) => setNewDatasetLabel(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newDatasetLabel.trim()) {
                handleCreate();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newDatasetLabel.trim() || creating}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Dataset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Dataset Label"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editLabel.trim()) {
                handleSaveRename();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRename} disabled={!editLabel.trim() || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title={`Delete "${datasetToDelete?.label}"?`}
        message="This will permanently delete this dataset. This action cannot be undone."
        confirmLabel="Delete"
        loading={deleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlateDatasets;
